import { createContext, useContext, useEffect, useState } from 'react';
import { MapRef } from 'react-map-gl';

export interface MarkerGlobalSettings {
    variant_icon: {
        largeStoryImageWidth: number;
        largeStoryImageHeight: number;
        mediumImageWidth: number;
        mediumImageHeight: number;
        collisionPaddingMiddle: number;
        collisionPaddingLarge: number;
        enableOrderNumberInSmall: boolean;
        enableOrderNumberInMedium: boolean;
    };
}

export interface MapFonts {
    regular: string;
    semiBold: string;
    bold: string;
}

export interface ICtxMapView {
    mapRef: React.RefObject<MapRef | null>;
    onMarkersClicked: (markerIds: string[]) => void;
    markerSettings: MarkerGlobalSettings;
    isWide: boolean;
    fonts: MapFonts;
    isEditMode: boolean;
    highlightedMarkerIds: string[];
    setForceHighlightSelectableMarkers: () => void;
    setHighlightedMarkerIds: (markerIds: string[]) => void;
    language: string;
    forceHighlightSelectableMarkers: boolean;
}

interface Props {
    mapRef: React.RefObject<MapRef | null>;
    onMarkersClicked: (markerIds: string[]) => void;
    children: JSX.Element;
}

export const CtxMapView = createContext<ICtxMapView | null>(null);

export const CtxMapViewProvider = (props: Props) => {
    const [highlightedMarkerIds, setHighlightedMarkerIds] = useState<string[]>([]);
    const [forceSelect, setForceSelect] = useState<boolean>(false);

    useEffect(() => {
        const timeout = setTimeout(() => {
            setForceSelect(false);
        }, 200);

        return () => {
            clearTimeout(timeout);
        };
    }, [forceSelect]);

    return (
        <CtxMapView.Provider
            value={{
                mapRef: props.mapRef,
                markerSettings: {
                    variant_icon: {
                        largeStoryImageWidth: 94,
                        largeStoryImageHeight: 70,
                        mediumImageWidth: 50,
                        mediumImageHeight: 50,
                        collisionPaddingMiddle: 60,
                        collisionPaddingLarge: 100,
                        enableOrderNumberInSmall: true,
                        enableOrderNumberInMedium: true
                    }
                },
                onMarkersClicked: (markerIds: string[]) => {
                    props.onMarkersClicked(markerIds);
                },
                isEditMode: true,
                highlightedMarkerIds,
                setHighlightedMarkerIds,
                setForceHighlightSelectableMarkers: () => {
                    setForceSelect(true);
                },
                isWide: true,
                fonts: {
                    regular: 'InterRegular',
                    semiBold: 'InterSemiBold',
                    bold: 'InterBold'
                },
                language: 'en',
                forceHighlightSelectableMarkers: forceSelect
            }}
        >
            {props.children}
        </CtxMapView.Provider>
    );
};

export const useCtxMapView = (): Required<ICtxMapView> => {
    const ctx = useContext(CtxMapView);
    if (!ctx) {
        throw new Error('useCtxMapView must be used within its context provider');
    }
    return ctx as Required<ICtxMapView>;
};
